import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TransitionGuideDrawer from "@/components/TransitionGuideDrawer";
import HelpLink from "@/components/HelpLink";
import { Compass, Sparkle, TrashSimple, CaretRight, ArrowClockwise, Wrench, CheckCircle } from "@phosphor-icons/react";

const SCENARIO_LABELS = {
  routine_declutter: "Routine Decluttering",
  smart_sizing: "Smart-Sizing",
  downsizing: "Downsizing",
  formal_dispensation: "Formal Asset Dispensation",
};

export default function TransitionGuide() {
  const { canWrite, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/transition/plans");
      setPlans(r.data?.plans || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load plans.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deletePlan = async (planId) => {
    if (!window.confirm("Delete this transition plan? This cannot be undone.")) return;
    try {
      await api.delete(`/transition/plans/${planId}`);
      toast.success("Plan deleted.");
      setPlans((p) => p.filter((x) => x.id !== planId));
      if (selectedPlan?.id === planId) setSelectedPlan(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed.");
    }
  };

  return (
    <div className="space-y-8" data-testid="transition-page">
      {/* Masthead */}
      <div>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3 flex items-center gap-3">
          <span>Advisory · Assets & Possessions</span>
          <HelpLink anchor="transition" label="About the Transition Guide" />
        </div>
        <div className="flex items-end justify-between border-b-2 border-[#C5A059] pb-6 gap-6 flex-wrap">
          <h1 className="font-serif-display text-5xl sm:text-6xl text-[#0A192F] leading-[0.95]">
            Transition<br />Guide.
          </h1>
          <div className="max-w-md">
            <p className="text-sm text-[#5C5F58] leading-relaxed">
              A multidisciplinary walkthrough for letting things go — routine tidying,
              smart-sizing, downsizing, or formal estate dispensation. Grounded in a curated
              knowledge base of tax, legal, environmental, and market sources.
            </p>
            {user?.jurisdiction && (
              <div className="mt-2 inline-block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Jurisdiction: <span className="text-[#0A192F] normal-case">{user.jurisdiction}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E0DDD6] bg-white">
        <div className="md:col-span-2 px-6 py-6">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
            Begin a new plan
          </div>
          <div className="font-serif-display text-2xl text-[#0A192F] mb-1">
            Answer seven short questions.
          </div>
          <p className="text-sm text-[#5C5F58]">
            Leo classifies the situation into one of four scenarios and composes plain-language
            guidance across six dimensions: financial, legal, emotional, logistical, environmental,
            and family coordination.
          </p>
        </div>
        <div className="px-6 py-6 border-t md:border-t-0 md:border-l border-[#E0DDD6] flex items-center justify-center bg-[#F9F8F6]">
          <button
            onClick={() => { setSelectedPlan(null); setDrawerOpen(true); }}
            disabled={!canWrite}
            data-testid="start-transition-btn"
            className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
          >
            <Sparkle size={16} weight="bold" /> Start a new plan
          </button>
        </div>
      </div>

      {/* Saved plans */}
      <section className="border border-[#E0DDD6] bg-white" data-testid="saved-plans">
        <header className="px-6 py-4 border-b border-[#E0DDD6] flex items-center justify-between">
          <div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Saved plans
            </div>
            <h2 className="font-serif-display text-2xl text-[#0A192F]">Your transition history</h2>
          </div>
          <button onClick={load} className="text-[#5C5F58] hover:text-[#1A3622]" title="Refresh" data-testid="refresh-plans-btn">
            <ArrowClockwise size={14} />
          </button>
        </header>
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-[#5C5F58]" data-testid="plans-loading">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Compass size={28} className="text-[#5C5F58] mx-auto mb-2" />
            <p className="text-sm text-[#5C5F58]">No plans yet. Begin your first one above.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E0DDD6]" data-testid="plans-list">
            {plans.map((p) => (
              <li key={p.id} className="px-6 py-4 flex items-start justify-between gap-4" data-testid={`plan-row-${p.id}`}>
                <button
                  onClick={() => setSelectedPlan(p)}
                  className="text-left flex-1 min-w-0"
                  data-testid={`plan-open-${p.id}`}
                >
                  <div className="text-sm text-[#0A192F] font-medium">
                    {SCENARIO_LABELS[p.scenario_key] || p.scenario_key}
                    {p.asset_snapshot?.name && <span className="text-[#5C5F58]"> · {p.asset_snapshot.name}</span>}
                  </div>
                  <div className="font-mono-ledger text-[11px] text-[#5C5F58] mt-1">
                    Saved {fmtDate(p.created_at)}
                    {p.jurisdiction && ` · ${p.jurisdiction}`}
                  </div>
                  {p.guide?.summary && (
                    <div className="text-xs text-[#5C5F58] mt-2 line-clamp-2">{p.guide.summary}</div>
                  )}
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => deletePlan(p.id)}
                    disabled={!canWrite}
                    data-testid={`plan-delete-${p.id}`}
                    className="text-[#6B1718] hover:text-[#4A0F10] disabled:opacity-40 p-1"
                    title="Delete plan"
                  >
                    <TrashSimple size={14} />
                  </button>
                  <CaretRight size={14} className="text-[#5C5F58]" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Read-only viewer for a selected plan */}
      {selectedPlan && (
        <PlanViewer plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
      )}

      <TransitionGuideDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onSaved={() => { load(); }}
      />
    </div>
  );
}

function PlanViewer({ plan, onClose }) {
  const { canWrite } = useAuth();
  const guide = plan.guide || {};
  const [scheduled, setScheduled] = useState({}); // key = `${dim.key}-${idx}` → true

  const schedule = async (dim, idx) => {
    const key = `${dim.key}-${idx}`;
    if (scheduled[key]) return;
    try {
      const r = await api.post(`/transition/plans/${plan.id}/actions/schedule`, {
        dimension_key: dim.key,
        action_index: idx,
      });
      setScheduled((prev) => ({ ...prev, [key]: r.data?.id || true }));
      toast.success(`Scheduled — due ${r.data?.due_date || "in 7 days"}. Open Maintenance to see it.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not schedule this action.");
    }
  };

  return (
    <section className="border border-[#0A192F] bg-white" data-testid="plan-viewer">
      <header className="px-6 py-4 border-b border-[#E0DDD6] flex items-center justify-between">
        <div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
            Plan detail
          </div>
          <h2 className="font-serif-display text-2xl text-[#0A192F]">
            {SCENARIO_LABELS[plan.scenario_key] || plan.scenario_key}
            {plan.asset_snapshot?.name && <span className="text-[#5C5F58] text-lg"> · {plan.asset_snapshot.name}</span>}
          </h2>
        </div>
        <button onClick={onClose} className="text-sm text-[#5C5F58] hover:text-[#1A3622]" data-testid="plan-viewer-close">
          Close
        </button>
      </header>
      <div className="px-6 py-6 space-y-4">
        {guide.summary && (
          <div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">Summary</div>
            <p className="text-sm text-[#0A192F]">{guide.summary}</p>
          </div>
        )}
        {guide.next_step && (
          <div className="bg-[#F9F8F6] border-l-2 border-[#C5A059] px-4 py-3">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">First next step</div>
            <p className="text-sm text-[#1A3622] font-medium">{guide.next_step}</p>
          </div>
        )}
        {(guide.dimensions || []).map((d) => (
          <div key={d.key} className="border border-[#E0DDD6] px-4 py-3" data-testid={`plan-dim-${d.key}`}>
            <div className="font-serif-display text-lg text-[#0A192F]">{d.label}</div>
            <p className="text-sm text-[#0A192F] mt-1">{d.narrative}</p>
            {(d.actions || []).length > 0 && (
              <ul className="mt-2 space-y-2">
                {d.actions.map((a, i) => {
                  const key = `${d.key}-${i}`;
                  const done = !!scheduled[key];
                  return (
                    <li key={key} className="text-xs text-[#0A192F] flex items-start gap-2" data-testid={`plan-action-${d.key}-${i}`}>
                      <div className="flex-1 min-w-0">
                        • <strong>{a.title}.</strong> <span className="text-[#5C5F58]">{a.detail}</span>
                      </div>
                      {canWrite && (
                        done ? (
                          <span
                            data-testid={`plan-action-scheduled-${d.key}-${i}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono-ledger uppercase tracking-[0.15em] text-[#1A3622] whitespace-nowrap shrink-0"
                          >
                            <CheckCircle size={12} weight="fill" /> Scheduled
                          </span>
                        ) : (
                          <button
                            onClick={() => schedule(d, i)}
                            data-testid={`plan-action-schedule-${d.key}-${i}`}
                            title="Add this action to your Maintenance list, due in 7 days"
                            className="inline-flex items-center gap-1 text-[10px] font-mono-ledger uppercase tracking-[0.15em] text-[#5C5F58] hover:text-[#1A3622] border border-[#E0DDD6] hover:border-[#1A3622] px-2 py-1 whitespace-nowrap shrink-0"
                          >
                            <Wrench size={11} /> Schedule
                          </button>
                        )
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {(d.citations || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {d.citations.map((c) => (
                  <span key={c} className="text-[10px] border border-[#E0DDD6] px-1.5 py-0.5 text-[#5C5F58]" data-testid={`plan-citation-${c}`}>{c}</span>
                ))}
              </div>
            )}
          </div>
        ))}
        {plan.user_notes && (
          <div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">Your notes</div>
            <p className="text-sm text-[#0A192F] whitespace-pre-line">{plan.user_notes}</p>
          </div>
        )}
      </div>
    </section>
  );
}
